import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import GoogleLogin from '../components/GoogleLogin';
import { LocalLink, useLocalizedNavigate } from '../i18n/useLocalizedNav';

const Login: React.FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const message = location.state?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/login', formData);
      if (res.type === 'EMAIL_NOT_VERIFIED') {
        // Password was correct but the email is still unverified — send
        // them to the OTP screen instead of showing a dead-end error.
        navigate('/verify-email', { state: { email: formData.email } });
      } else if (res.error) {
        setError(res.error);
      } else {
        login(res.user);
        navigate('/');
      }
    } catch (err) {
      setError(t('auth_err_invalid_credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-warm-200 shadow-sm space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-heading font-bold text-warm-800">{t('login_welcome')}</h2>
        <p className="text-warm-500">{t('login_subtitle')}</p>
      </div>

      {message && <div className="bg-brand-success/20 text-brand-success p-3 rounded-xl text-center font-bold text-sm">{message}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_email_label')}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input 
              type="email" 
              placeholder="name@example.com"
              className="w-full pl-10 pr-4 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_password_label')}</label>
            <LocalLink to="/forgot-password" className="text-xs text-brand-accent font-bold hover:underline">{t('login_forgot')}</LocalLink>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input 
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth_hide_password') : t('auth_show_password')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {error && <p className="text-brand-danger text-sm font-bold">{error}</p>}

        <button 
          type="submit" 
          disabled={isLoading}
          className="w-full py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('login_submit')}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-warm-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-warm-500">{t('auth_or_continue')}</span>
        </div>
      </div>

      <GoogleLogin />

      <p className="text-center text-warm-500 text-sm">
        {t('login_no_account')} <LocalLink to="/register" className="text-brand-accent font-bold">{t('login_create_one')}</LocalLink>
      </p>
    </div>
  );
};

export default Login;
